import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Search, Eye, Download, ArrowRight, Send, X, Loader2, 
  CheckCircle, AlertTriangle, Image as ImageIcon
} from 'lucide-react';
import { ParticlesBackground } from '../components/ParticlesBackground';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StrategyCard({ strategy }) {
  return (
    <Link to={`/strategy-lab/${strategy.slug}`} data-testid={`strategy-card-${strategy.slug}`}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -4 }}
        className="rounded-2xl overflow-hidden border border-white/5 hover:border-white/10 transition-all cursor-pointer group bg-[#0a0a0f]"
      >
        {/* Banner Image */}
        <div className="aspect-video bg-gradient-to-br from-[#8B5CF6]/20 to-[#007AFF]/20 relative overflow-hidden">
          {strategy.banner_image ? (
            <img
              src={strategy.banner_image.startsWith('monkalgo/') 
                ? `${API_URL}/api/strategy-lab/files/${strategy.banner_image}`
                : `${API_URL}/api/strategy-lab/images/${strategy.banner_image}`}
              alt={strategy.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              key={strategy.banner_image}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-12 h-12 text-white/10" />
            </div>
          )}
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        </div>

        {/* Content */}
        <div className="p-5">
          <h3 className="font-heading font-semibold text-white text-lg mb-2 group-hover:text-[#8B5CF6] transition-colors">
            {strategy.name}
          </h3>
          
          {strategy.description && (
            <p className="text-white/50 text-sm line-clamp-2 mb-4">
              {strategy.description}
            </p>
          )}

          {/* Tags & Stats */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {strategy.instrument && (
                <span className="px-2.5 py-1 rounded-lg bg-[#8B5CF6]/10 text-[#8B5CF6] text-xs font-medium">
                  {strategy.instrument}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-white/30 text-xs">
              <span className="flex items-center gap-1">
                <Eye size={12} /> {strategy.view_count || 0}
              </span>
              <span className="flex items-center gap-1">
                <Download size={12} /> {strategy.download_count || 0}
              </span>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-4 pt-4 border-t border-white/5">
            <span className="text-[#8B5CF6] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
              View & Download <ArrowRight size={14} />
            </span>
          </div>
        </div>
      </motion.div>
    </Link>
  );
}

function SubmitStrategyModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({ email: '', name: '', strategy_name: '', strategy_source: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.name || !formData.strategy_name || !formData.strategy_source) {
      setError('Please fill in all required fields');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // For now, just show success - can integrate with backend later
      setSuccess(true);
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative w-full max-w-lg bg-[#0a0a0f] rounded-2xl border border-white/10 shadow-2xl"
      >
        <div className="p-6 border-b border-white/10">
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center">
            <X size={16} className="text-white/60" />
          </button>
          <h2 className="font-heading font-bold text-xl text-white">Submit a Strategy</h2>
          <p className="font-body text-sm text-white/50 mt-1">Share a strategy you want us to review</p>
        </div>

        {success ? (
          <div className="p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#10B981]" />
            </div>
            <h3 className="font-heading font-semibold text-lg text-white mb-2">Strategy Submitted!</h3>
            <p className="font-body text-sm text-white/50">We&apos;ll review it and get back to you.</p>
            <button onClick={onClose} className="mt-6 px-6 py-2.5 bg-white/5 hover:bg-white/10 rounded-xl text-white text-sm font-medium transition-colors">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-body text-xs text-white/50 mb-1.5 block">Your Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="font-body text-xs text-white/50 mb-1.5 block">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50"
                  placeholder="john@example.com"
                />
              </div>
            </div>
            <div>
              <label className="font-body text-xs text-white/50 mb-1.5 block">Strategy Name *</label>
              <input
                type="text"
                value={formData.strategy_name}
                onChange={(e) => setFormData(prev => ({ ...prev, strategy_name: e.target.value }))}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50"
                placeholder="ICT Silver Bullet"
              />
            </div>
            <div>
              <label className="font-body text-xs text-white/50 mb-1.5 block">Source URL or Description *</label>
              <input
                type="text"
                value={formData.strategy_source}
                onChange={(e) => setFormData(prev => ({ ...prev, strategy_source: e.target.value }))}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50"
                placeholder="https://youtube.com/... or @creator_handle"
              />
            </div>
            <div>
              <label className="font-body text-xs text-white/50 mb-1.5 block">Additional Notes (optional)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50 resize-none"
                placeholder="Any notes about the strategy..."
              />
            </div>
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertTriangle size={16} className="text-red-400" />
                <span className="text-sm text-red-400">{error}</span>
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-gradient-to-r from-[#8B5CF6] to-[#007AFF] hover:from-[#9B6CF6] hover:to-[#1A8AFF] rounded-xl text-white font-heading font-semibold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-70"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              {loading ? 'Submitting...' : 'Submit Strategy'}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}

export default function StrategyLabPage() {
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchStrategies();
  }, []);

  const fetchStrategies = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/strategy-lab/strategies`);
      const data = await res.json();
      setStrategies(data.strategies || []);
    } catch (err) {
      console.error('Failed to fetch strategies:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredStrategies = strategies.filter(s => 
    !searchQuery || 
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.instrument?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#050508]">
      {/* Hero Section */}
      <section className="relative pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0"><ParticlesBackground /></div>
        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-1.5 rounded-full glass text-[#8B5CF6] text-xs font-mono mb-4">
              FREE TRADING STRATEGIES
            </span>
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-white mb-4">
              Strategy Lab
            </h1>
            <p className="font-body text-lg text-white/50 mb-8">
              Download free Pine Scripts, TradingView indicators, and trading strategies curated by Monk Algo.
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search strategies..."
                className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#8B5CF6]/50 transition-colors"
                data-testid="strategy-search-input"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20">
        <div className="max-w-6xl mx-auto px-6">
          {/* Submit Strategy CTA */}
          <div className="flex justify-end mb-8">
            <button
              onClick={() => setShowSubmitModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white text-sm font-medium transition-colors"
              data-testid="submit-strategy-btn"
            >
              <Send size={16} />
              Submit a Strategy
            </button>
          </div>

          {/* Strategy Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#8B5CF6] animate-spin" />
            </div>
          ) : filteredStrategies.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-white/20" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-white mb-2">
                {searchQuery ? 'No strategies found' : 'Coming Soon'}
              </h3>
              <p className="font-body text-sm text-white/40 mb-6">
                {searchQuery ? 'Try a different search term' : 'New strategies are being added regularly. Check back soon!'}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="strategy-grid">
              {filteredStrategies.map((strategy, index) => (
                <motion.div
                  key={strategy.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <StrategyCard strategy={strategy} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6">
          <p className="font-body text-[11px] text-white/30 leading-relaxed text-center">
            <strong className="text-white/40">Disclaimer:</strong> Strategies shared here are for educational purposes only. 
            Past performance does not guarantee future results. Trading carries significant risk of loss. 
            Always conduct your own research before making trading decisions.
          </p>
        </div>
      </section>

      {/* Submit Strategy Modal */}
      <SubmitStrategyModal isOpen={showSubmitModal} onClose={() => setShowSubmitModal(false)} />
    </div>
  );
}
